import {
	clone,
	emailAddress,
	getPropValidator,
	hasValue,
	max,
	min,
	MultiValidatorFn,
	mustBeTrueIf,
	mustBeTrueMsg,
	noValue,
	phoneNumber,
	requiredIf,
	testRegex,
	ValidatorFn,
	zipCode,
} from "@repo/helpers";
import { address, keyPair, SerializedFile } from "@repo/models";

import { component, conditionalField } from "../models/component";
import { componentType } from "../models/enums";
import { ComponentValidationResult, MappedComponent, MappedDyno } from "../models/mappedComponent";
import { localizeTraversedName, traverseProperty } from "./dynamicProperties";
import { Templator } from "./template";

const VALID_PROMPT_EMAIL = "Please enter a valid email address";
const VALID_PROMPT_PHONE = "Please enter a valid phone number";
const VALID_PROMPT_NUMBER = "Please enter a valid number";

export enum DateRange {
	past = 0,
	current = 1,
	future = 2,
	adultDoB = 3,
	dateOfBirth = 4,
	year = 5,
	month = 6,
	day = 7,
}

interface Files {
	values: SerializedFile[];
	uploadedValues: SerializedFile[];
}

export function validateC(component: MappedComponent) {
	const VALID_PROMPT_TEXT = `Invalid ${component.label}`;
	const VALID_PROMPT_DOC_REVIEW = `${component.label} must be reviewed`;
	return {
		component: validComponent(),
		selector: validSelector(),
		boolean: validComponent<boolean>(mustBeTrueIf(required(component)(null), component.label)),
		email: validComponent(validRegex(component, VALID_PROMPT_EMAIL), emailAddress),
		phone: validComponent(validRegex(component, VALID_PROMPT_PHONE), phoneNumber),
		number: validComponent<number>(
			validRegex(component, VALID_PROMPT_NUMBER),
			min(component.extendedProperties.min, component.label),
			max(component.extendedProperties.max, component.label),
		),
		text: validComponent(validRegex(component, VALID_PROMPT_TEXT)),
		textArea: validComponent(validRegex(component, VALID_PROMPT_TEXT), textLength),
		zipCode: validComponent(zipCode),
		date: validComponent(date),

		fileUpload: validComponent(fileUpload),
		documentReview: validComponent(mustBeTrueMsg(VALID_PROMPT_DOC_REVIEW)),
		addressAutocomplete: validComponent(addressAutocomplete),
		entryTextWithValidation: validComponent(entryTextWithValidation),
	};

	function validComponent<TProp = string>(...validators: ValidatorFn<TProp>[]) {
		if (!component.visible || component.readOnly) return (val: TProp) => null;
		return getPropValidator<TProp>(required(component), ...validators);
	}

	function validSelector<TProp = string>(...validators: ValidatorFn<TProp>[]) {
		if (!component.visible || component.readOnly) return (val: TProp) => null;

		return getPropValidator<TProp>(selectorRequired(), ...validators);

		function selectorRequired() {
			return required(component);
		}
	}

	function date(value: Date | string) {
		if (!value) return null;
		const dateValue = new Date(value);
		//Gets date only portion
		const today = new Date(new Date().toLocaleString().split(",")[0]);
		const dob18Years = new Date();
		dob18Years.setFullYear(dob18Years.getFullYear() - 18);
		const label = component.label.replace(":", "");
		if (component.optional === DateRange.past.toString() && dateValue >= today) {
			return `${label} must be before today`;
		} else if (component.optional === DateRange.current.toString() && dateValue < today) {
			return `${label} must be on or after today.`;
		} else if (component.optional === DateRange.future.toString() && dateValue <= today) {
			return `${label} must be after today.`;
		} else if (component.optional === DateRange.adultDoB.toString() && dateValue >= dob18Years) {
			return `${label} must be more than 18 years ago`;
		} else if (dateValue.getFullYear() < 1800) {
			return `${label} invalid`;
		}
		return null;
	}

	function fileUpload(uploads: Files) {
		if (!uploads) return null;
		let totalFiles = 0;
		if (uploads.values && uploads.values.length) {
			totalFiles += uploads.values.length;
		}
		if (uploads.uploadedValues?.length) {
			totalFiles += uploads.uploadedValues.filter((v) => !v.deleted).length;
		}
		if (component.required && totalFiles < 1) {
			return `At least 1 file is required`;
		}
		if (hasValue(component.extendedProperties.min) && totalFiles < component.extendedProperties.min) {
			return `At least ${component.extendedProperties.min} file${component.extendedProperties.min > 1 ? "s" : ""} required`;
		}
		if (hasValue(component.extendedProperties.max) && totalFiles > component.extendedProperties.max) {
			return `${component.extendedProperties.max} files maximum`;
		}
		return null;
	}

	/** Empty values are left to the required check, so min only applies once something has been entered */
	function textLength(value: string) {
		const length = value?.length ?? 0;
		if (!length) return null;
		const { minLength, maxLength } = component.extendedProperties;
		const label = component.label.replace(":", "");
		if (hasValue(minLength) && length < minLength) return `${label} must be at least ${minLength} characters`;
		if (hasValue(maxLength) && length > maxLength) return `${label} must be ${maxLength} characters or fewer`;
		return null;
	}

	function addressAutocomplete(value: address) {
		if (!value) return "Please enter an address";

		if (value.address1 && value.city && value.stateName && value.zip) {
			return null;
		}

		return "Please enter an address";
	}

	function entryTextWithValidation(value: any) {
		const { label, extensionPropertyValue } = component;
		const validationResult = getPropValidator(required(component), validRegex(component, VALID_PROMPT_TEXT))(value);
		const { success, message } = extensionPropertyValue ?? { success: undefined, message: undefined };
		if (validationResult) return validationResult;
		if (success === null || success === undefined) return `${label} has not been validated`;
		if (!success) return `${label} is invalid: ${message}`;
		return null;
	}
}

function validRegex(component: MappedComponent, validPrompt: string) {
	return testRegex(component.extendedProperties.regex, validPrompt);
}

export function validConfirmation(component: MappedComponent, confirmValue: any) {
	const VALID_PROMPT = `${component.label} does not match`;
	return (value: string) => (value === confirmValue ? null : VALID_PROMPT);
}

export function getValidator(component: MappedComponent): MultiValidatorFn<any, ComponentValidationResult> {
	const validate = validateC(component);
	switch (component.type as any) {
		case componentType.entryText:
			return validate.text;
		case componentType.entryTextArea:
			return validate.textArea;
		case componentType.entryTextWithValidation:
			return validate.entryTextWithValidation;
		case componentType.entryNumber:
		case componentType.entryCurrency:
			return validate.number;
		case componentType.entryPhone:
			return validate.phone;
		case componentType.entryEmailAddress:
			return validate.email;
		case componentType.entryAddress:
			return validateChildren as any;
		case componentType.entryAddressAutocomplete:
			return validate.addressAutocomplete;
		case componentType.entryZip:
			return validate.zipCode;
		case componentType.entryCheckbox:
		case componentType.entryLabeledCheckbox:
		case componentType.entryBoolean:
			return validate.boolean;
		case componentType.entryDropdown:
		case componentType.radioButtonList:
		case componentType.panelSelector:
			if (component.extendedProperties.hasOtherField && component.extendedProperties.isOtherSelected)
				return validateChildren as any;
			return validate.selector;
		case componentType.documentReview:
			return validate.documentReview;
		case componentType.entrySignDocument:
			return validateChildren as any;
		case componentType.entryDate:
		case componentType.dateSelector:
		case componentType.monthSelector:
			return validate.date;
		case componentType.fileUpload:
		case componentType.documentTypeUpload:
			return validate.fileUpload;
		case componentType.entryTextAutocomplete:
		case componentType.entryTime:
		case componentType.entrySecret:
		case componentType.entryNullableBoolean:
		case componentType.photoCapture:
		case componentType.audioRecording:
		case componentType.mobilePhotoCapture:
		case componentType.blinkId:
		case componentType.entrySignature:
		case componentType.googleMapCapture:
			return validate.component;
	}
	return requiredIf(false);

	function validateChildren<TProp>(prop: TProp) {
		return component.components.map((c) => {
			return {
				component: c,
				value: getValidator(c)(c.propertyValue) as string,
			} as ComponentValidationResult;
		});
	}
}

export function requiredField(component: MappedComponent) {
	return getPropValidator<any>(required(component));
}

export function required(component: MappedComponent) {
	if (!component.visible || component.readOnly) return (o: any) => null;
	return requiredIf(component.required, component.label, component.extendedProperties.validationMessage?.required);
}

/**
 * Returns true if condition matches, and false if not
 * Returns default value if no condition set
 * @param condition Conditional field to check
 * @param props Full set of data to compare conditions against
 * @param defaultValue What to return if no condition
 */
export const matchConditional = getMatchConditional();
export const shouldBeDisplayed = (props: any) => (component: MappedDyno) => {
	return matchConditional(component.visibleFor, props, true);
};

function matchConditionalInternal(condition: conditionalField, props: any) {
	if (!condition) return true;

	function propertyIsMatch(property: keyPair) {
		const id = property.id as string;

		const value = traverseProperty(id, props);
		function isMatch(v: any) {
			const strV = String(v);
			const strValue = typeof value === "object" ? JSON.stringify(value) : String(value);
			if (strV === strValue) return true;
			if (hasValue(value) && strValue.split(",").includes(strV)) return true;
			switch (strV) {
				case "falsy":
					return !value;
				case "null":
					return !hasValue(value);
				case "nullOrEmpty":
					return noValue(value);
				case "any":
					return hasValue(value);
			}
			return false;
		}

		const arrayValue = tryGetArrayValue(property.value);
		if (arrayValue) return arrayValue.some(isMatch);

		//"Any" means any value.  Using nonzero, as our id props default to 0
		const template = Templator(props, props, property.value, null, true);
		const { values } = template.breakdown();
		const templateValue = template.buildString(values);
		const propertyValue = templateValue ?? values[0] ?? property.value;

		const templateArrayValue = tryGetArrayValue(templateValue);
		if (templateArrayValue) return templateArrayValue.some(isMatch);

		return isMatch(propertyValue);
	}

	if (condition.property) return condition.property.every(propertyIsMatch);
	return true;
}
/** JSON.parse can only produce an array from text that starts with "[" after JSON whitespace */
const JSON_ARRAY_START = /^[\t\n\r ]*\[/;
/** Parsed condition arrays by source text (conditions are re-evaluated on every remap) */
const parsedArrayValues = new Map<string, any[] | null>();
const MAX_PARSED_ARRAY_VALUES = 1000;

function tryGetArrayValue(value: any) {
	//Try/Catch is here b/c JSON parse doesn't handle straight string values
	try {
		// JSON.parse reads String(value); skip it (and the exception it throws) when that can't be an array
		// oxlint-disable-next-line typescript/no-base-to-string -- intentionally mirrors JSON.parse's coercion
		const text = String(value);
		if (!JSON_ARRAY_START.test(text)) return null;
		if (parsedArrayValues.has(text)) return parsedArrayValues.get(text);

		let arrayValue: any[] | null = null;
		try {
			const parsed = JSON.parse(text);
			if (Array.isArray(parsed)) arrayValue = parsed;
		} catch {
			arrayValue = null;
		}
		if (parsedArrayValues.size >= MAX_PARSED_ARRAY_VALUES) parsedArrayValues.clear();
		parsedArrayValues.set(text, arrayValue);
		return arrayValue;
	} catch (ex: any) {
		return null;
	}
}

export function getMatchConditional<TProps = any>(
	extendedMatch?: (condition: conditionalField, props: TProps) => boolean,
) {
	function isMatch(props: TProps, defaultValue: boolean) {
		function isMatchInternal(condition: conditionalField) {
			if (!condition) return defaultValue;
			switch (condition.operator) {
				case "and":
					return condition.children.every(isMatchInternal);
				case "nand":
					return !condition.children.every(isMatchInternal);
				case "or":
					return condition.children.some(isMatchInternal);
				case "nor":
					return !condition.children.some(isMatchInternal);
				case "xor":
					return condition.children.filter(isMatchInternal).length === 1;
				case "not":
					return !isMatchSelf();
				case "is":
					return isMatchSelf();
				default:
					//Temporary code for transition, outcome should be deprecated and "is" should be default
					return isMatchSelf() ? condition.outcome : !condition.outcome;
			}

			function isMatchSelf() {
				const baseMatch = matchConditionalInternal(condition, props);
				const sMatch = !extendedMatch || extendedMatch(condition, props);
				return baseMatch && sMatch;
			}
		}
		return isMatchInternal;
	}
	return (condition: conditionalField, props: TProps, defaultValue = false) =>
		isMatch(props, defaultValue)(condition);
}

/** Updates conditional fields to map to properties inside section */
export function getSubSectionConditionalFor(sectionPropertyName: string, index: number, component: component) {
	return getSectionConditionalFor(`${sectionPropertyName}[${index}]`, component);
}

export function getSectionConditionalFor(sectionPropertyName: string, component: component) {
	const mapConditional = (property: keyPair) => {
		const id = localizeTraversedName(property.id as string, sectionPropertyName);

		return { id, value: property.value };
	};
	const mapProperties = (field: conditionalField) => {
		if (!field) return field;
		field = clone(field);
		if (field.property) field.property = field.property.map(mapConditional);
		if (field.children) field.children = field.children.map(mapProperties);
		return field;
	};

	const visibleFor = mapProperties(component.visibleFor);
	const requiredFor = mapProperties(component.requiredFor);
	const disabledFor = mapProperties(component.disabledFor);

	return {
		visibleFor,
		requiredFor,
		disabledFor,
	};
}

//gets either label or derived label if necessary
export function getDerivedLabel(props: any, name: string, parentPropertyName?: string) {
	let label = name;
	if (!label) return "";
	if (label.startsWith("prop:")) {
		label = label.replace("prop:", "");
		label = localizeTraversedName(label, parentPropertyName);
		label = traverseProperty<string>(label, props);
	}
	return label;
}
